// src/components/orders/wizard/ProductStep.js
import React, { useState, useEffect } from 'react';
import {
  Package, Search, Plus, Minus, Trash2, ArrowRight, ArrowLeft,
  ShoppingCart, AlertCircle, WifiOff
} from 'lucide-react';
import { useProducts } from '../../../hooks/useApi';
import { useOfflineStorage } from '../../../hooks/useOfflineStorage';

const ProductStep = ({
  data,
  updateData,
  validateStep,
  validations,
  nextStep,
  prevStep,
  invalidateStepsAfter,
  currentStep,
  setCurrentStep,
  updateOrderItems
}) => {
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productQuantity, setProductQuantity] = useState(1);
  const [showProductSearch, setShowProductSearch] = useState(false);

  const { isOnline } = useOfflineStorage();
  const { products, loading: productsLoading } = useProducts(productSearchTerm);

  // Mostra tutti i prodotti quando si va offline
  useEffect(() => {
    if (!isOnline) {
      setShowProductSearch(true);
    }
  }, [isOnline]);

  const handleAddProduct = (product, quantity = 1) => {
    const existingItemIndex = data.orderItems?.findIndex(
      (item) => item.product.id === product.id
    ) ?? -1;

    let newItems;
    if (existingItemIndex >= 0) {
      newItems = [...data.orderItems];
      newItems[existingItemIndex].quantita += quantity;
      newItems[existingItemIndex].imponibile =
        newItems[existingItemIndex].quantita * newItems[existingItemIndex].prezzo;
    } else {
      const newItem = {
        id: Date.now(),
        product,
        codice: product.code,
        descrizione: product.description,
        pesoGr: product.weightGr,
        um: product.unitMeasure,
        quantita: quantity,
        prezzo: parseFloat(product.price),
        imponibile: quantity * parseFloat(product.price),
        iva: parseInt(
          product.vatRate === 'FOUR' ? 4 :
            product.vatRate === 'TEN' ? 10 : 22
        ),
        confezione: product.packaging,
      };
      newItems = [...(data.orderItems || []), newItem];
    }

    updateOrderItems(newItems);
    updateData({ orderItems: newItems });
    setSelectedProduct(null);
    setProductQuantity(1);

    // Valida step automaticamente
    setTimeout(() => validateStep(2, validations.products), 100);
  };

  const handleUpdateItem = (itemId, field, value) => {
    const updatedItems = data.orderItems.map((item) => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantita' || field === 'prezzo') {
          updatedItem.imponibile = updatedItem.quantita * updatedItem.prezzo;
        }
        return updatedItem;
      }
      return item;
    });

    updateOrderItems(updatedItems);
    updateData({ orderItems: updatedItems });

    // Se non ci sono più articoli con quantità > 0, invalida step successivi
    const hasValidItems = updatedItems.some(item => item.quantita > 0);
    if (!hasValidItems) {
      invalidateStepsAfter(2); // Invalida tutto dopo lo step prodotti
    }
  };

  const handleRemoveItem = (itemId) => {
    if (window.confirm('Rimuovere questo articolo?')) {
      const newItems = data.orderItems.filter((item) => item.id !== itemId);
      updateOrderItems(newItems);
      updateData({ orderItems: newItems });

      // Se non ci sono più articoli, invalida step successivi e forza il ritorno
      if (newItems.length === 0) {
        invalidateStepsAfter(2); // Invalida tutto dopo lo step prodotti

        // Se siamo oltre lo step 2, torna indietro forzatamente
        if (currentStep > 2) {
          setTimeout(() => {
            setCurrentStep(2);
          }, 100);
        }
      }
    }
  };

  const totalAmount = data.orderItems?.reduce((sum, item) => sum + (item.imponibile || 0), 0) || 0;
  const isValid = validations.products(data);

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="text-center mb-6 lg:mb-8">
        <div className="w-16 h-16 lg:w-20 lg:h-20 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#fcfbd6' }}>
          <Package className="w-8 h-8 lg:w-10 lg:h-10" style={{ color: '#c48f75' }} />
        </div>
        <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-2">
          Aggiungi Prodotti
        </h2>
        <p className="text-gray-600 text-sm lg:text-base">
          Cerca e aggiungi i prodotti per questo ordine
        </p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 lg:gap-8">
        {/* Ricerca Prodotti */}
        <div className="space-y-6">
          <div className="rounded-xl p-6" style={{ backgroundColor: '#fcfbd6' }}>
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Search className="w-5 h-5 mr-2" style={{ color: '#c48f75' }} />
              Cerca Prodotto
            </h3>

            {/* Search Input */}
            <div className="relative mb-4">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={isOnline ? "Cerca per codice o descrizione..." : "Prodotti disponibili offline"}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 transition-colors login-input"
                value={productSearchTerm}
                onChange={(e) => {
                  const newValue = e.target.value;
                  setProductSearchTerm(newValue);
                  if (!isOnline) {
                    // Se offline, mostra sempre i prodotti (anche senza termine di ricerca)
                    setShowProductSearch(true);
                  } else {
                    // Se online, mostra solo se c'è testo
                    setShowProductSearch(newValue.length > 0);
                  }
                }}
                onFocus={() => {
                  // Mostra sempre i prodotti al focus, sia online che offline
                  setShowProductSearch(true);
                }}
              />
            </div>

            {/* Product Results */}
            {showProductSearch && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {!isOnline && (
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3 flex items-center space-x-2">
                    <WifiOff className="w-4 h-4 text-orange-600" />
                    <span className="text-xs text-orange-700 font-medium">Modalità offline - Dati cache</span>
                  </div>
                )}
                {productsLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 mx-auto" style={{ borderColor: '#c48f75' }}></div>
                  </div>
                ) : products.length > 0 ? (
                  products
                    .sort((a, b) => (a.description || '').localeCompare(b.description || ''))
                    .map((product) => (
                      <button
                        key={product.id}
                        onClick={() => setSelectedProduct(product)}
                        className={`w-full p-3 text-left border rounded-lg transition-colors ${selectedProduct?.id === product.id
                          ? 'text-gray-900'
                          : 'border-gray-200'
                          }`}
                        style={selectedProduct?.id === product.id ?
                          { backgroundColor: '#fcfbd6', borderColor: '#fcfbd6' } : {}}
                        onMouseEnter={(e) => {
                          if (selectedProduct?.id !== product.id) {
                            e.target.style.backgroundColor = '#fcfbd6';
                            e.target.style.borderColor = '#fcfbd6';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (selectedProduct?.id !== product.id) {
                            e.target.style.backgroundColor = '';
                            e.target.style.borderColor = '';
                          }
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono">
                                {product.code}
                              </span>
                              <span className={`text-sm font-bold ${selectedProduct?.id === product.id ? 'text-gray-900' : 'text-gray-700'
                                }`}>
                                €{(parseFloat(product.price) || 0).toFixed(2)}
                              </span>
                            </div>
                            <p className={`text-sm font-medium truncate ${selectedProduct?.id === product.id ? 'text-gray-900' : 'text-gray-900'
                              }`}>
                              {product.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))
                ) : (
                  <div className="text-center py-4">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50 text-gray-400" />
                    <p className="text-gray-500 font-medium">Nessun prodotto trovato</p>
                    {!isOnline && (
                      <p className="text-sm text-orange-600 mt-2">Assicurati che i prodotti siano stati precaricati</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Suggerimento per modalità offline */}
            {!showProductSearch && !isOnline && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <WifiOff className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-orange-900">Modalità Offline</h4>
                    <p className="text-sm text-orange-700 mt-1">
                      Clicca nel campo di ricerca per vedere tutti i prodotti disponibili offline
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Selected Product */}
            {selectedProduct && (
              <div className="mt-6 p-4 bg-white rounded-lg border wizard-button-border">
                <h4 className="font-medium text-gray-900 mb-2">Prodotto Selezionato</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="px-2 py-1 rounded text-xs font-mono text-white" style={{ backgroundColor: '#c48f75' }}>
                      {selectedProduct.code}
                    </span>
                    <span className="text-lg font-bold" style={{ color: '#c48f75' }}>
                      €{(parseFloat(selectedProduct.price) || 0).toFixed(2)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700">{selectedProduct.description}</p>

                  {/* Quantity Selector */}
                  <div className="flex items-center space-x-3 mt-4">
                    <label className="text-sm font-medium text-gray-700">Quantità:</label>
                    <button
                      onClick={() => setProductQuantity(Math.max(1, productQuantity - 1))}
                      className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      min="1"
                      value={productQuantity}
                      onChange={(e) => setProductQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 text-center border border-gray-300 rounded-lg py-1"
                    />
                    <button
                      onClick={() => setProductQuantity(productQuantity + 1)}
                      className="w-8 h-8 rounded-full flex items-center justify-center transition-colors text-white"
                      style={{ backgroundColor: '#c48f75' }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#4c4849'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#c48f75'}
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>

                  <button
                    onClick={() => handleAddProduct(selectedProduct, productQuantity)}
                    className="w-full mt-4 text-white py-2 rounded-lg transition-colors"
                    style={{ backgroundColor: '#c48f75' }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#4c4849'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#c48f75'}
                  >
                    Aggiungi all'Ordine
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Carrello Ordine */}
        <div>
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <ShoppingCart className="w-5 h-5 mr-2" style={{ color: '#c48f75' }} />
              Carrello Ordine ({data.orderItems?.length || 0} articoli)
            </h3>

            {data.orderItems?.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {data.orderItems.map((item) => (
                  <div key={item.id} className="bg-white p-4 rounded-lg border">
                    <div className="flex items-center justify-between mb-2">
                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-xs font-mono">
                        {item.codice}
                      </span>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleRemoveItem(item.id)}
                          className="text-red-500 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-2">{item.descrizione}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          min="1"
                          value={item.quantita}
                          onChange={(e) => handleUpdateItem(item.id, 'quantita', parseInt(e.target.value) || 1)}
                          className="w-16 text-center border border-gray-300 rounded py-1 text-sm"
                        />
                        <span className="text-xs text-gray-500">× €{(item.prezzo || 0).toFixed(2)}</span>
                      </div>
                      <span className="font-bold" style={{ color: '#c48f75' }}>€{(item.imponibile || 0).toFixed(2)}</span>
                    </div>
                  </div>
                ))}

                {/* Totale */}
                <div className="p-4 rounded-lg border-2 wizard-button-border" style={{ backgroundColor: '#fcfbd6' }}>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">Totale Ordine:</span>
                    <span className="text-xl font-bold" style={{ color: '#c48f75' }}>€{(totalAmount || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ShoppingCart className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>Nessun articolo aggiunto</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between items-center mt-8">
        <button
          onClick={prevStep}
          className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 sm:px-6 lg:px-8 py-3 rounded-xl font-medium hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Indietro</span>
        </button>
        <button
          onClick={() => {
            if (isValid) {
              validateStep(2, validations.products, data);
              nextStep();
            }
          }}
          disabled={!isValid}
          className={`flex items-center space-x-2 px-4 sm:px-6 lg:px-8 py-3 rounded-xl font-medium transition-all ${isValid
            ? 'text-white shadow-lg'
            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          style={isValid ? { backgroundColor: '#c48f75' } : {}}
          onMouseEnter={(e) => isValid && (e.target.style.backgroundColor = '#4c4849')}
          onMouseLeave={(e) => isValid && (e.target.style.backgroundColor = '#c48f75')}
        >
          <span>Continua</span>
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      {/* Validation Message */}
      {!isValid && (
        <div className="mt-4 flex items-center text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
          <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
          <span>Aggiungi almeno un prodotto per continuare</span>
        </div>
      )}
    </div>
  );
};

export default ProductStep;